import Modal from "components/EvtKit/modal/Modal"

import { useModal } from "hooks/useModal"
import useAccountForm from "pages/accounts/hooks/useAccountForm"
import useAccountRequest from "pages/accounts/hooks/useAccountRequest"

import Switch from "components/EvtKit/switch/Switch"
import { useCallback, useEffect } from "react"
import styled from "styled-components"
import Heading from "components/Typography/Heading"
import BodyText from "components/Typography/BodyText"
import Button from "components/EvtKit/button/Button"
import clsx from "clsx"
import { clearAccount } from "reducers/accounts/accounts-actions"
import { useAppDispatch } from "reducers/store"

const ModalContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 8px;
  gap: 32px;
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
`

const ModalFooterContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  gap: 20px;
  margin: 0 auto;
`

const ButtonContainer = styled.div`
  width: 160px;
`

const SwitchButton = styled.button`
  height: 24px;
  width: 45px;
`

const SwitchContent = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const SwitchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

interface AccountsModalProps {
  initialData: App.Account
}

const modalTitles = {
  delete: "Deletar Conta",
  create: "Criar Conta",
  update: "Editar Conta"
}

const submitAction = {
  delete: "delete",
  create: "create",
  update: "update"
}

const AccountsModal = (props: AccountsModalProps) => {
  const { initialData } = props

  const appDispatch = useAppDispatch()

  const { isOpen, type, closeModal } = useModal()

  const { formData, handleToggle, overwriteFormData, resetFormData } = useAccountForm({
    isEnterprise: false,
    shouldPayByInvoice: false
  })

  useEffect(() => {
    if (initialData && (type === "update" || type === "delete")) {
      overwriteFormData({ isEnterprise: initialData.isEnterprise, shouldPayByInvoice: initialData.shouldPayByInvoice })
    }
  }, [initialData, type])

  const handleCloseModal = useCallback(() => {
    resetFormData()
    appDispatch(clearAccount())
    closeModal()
  }, [])

  const { onSubmit, isLoading } = useAccountRequest(initialData.id, submitAction[type], handleCloseModal)

  const handleSubmit = useCallback(() => {
    onSubmit({
      ...formData,
      isEnterprise: formData.isEnterprise || false,
      shouldPayByInvoice: formData.shouldPayByInvoice || false
    })
  }, [formData])

  return (
    <Modal
      openModal={(type === "create" || type === "update" || type === "delete") && isOpen}
      closeModal={handleCloseModal}
      size={"sm"}
    >
      <ModalContent>
        <ModalHeader>
          <Heading variant="h4" color="primary-dark">
            {modalTitles[type]}
          </Heading>
        </ModalHeader>

        {(type === "create" || type === "update") && (
          <SwitchContainer>
            <SwitchContent>
              <BodyText size="medium" weight="semi-bold">
                Esta conta é “enterprise”?
              </BodyText>
              <SwitchButton name="isEnterprise" type="button" onClick={() => handleToggle("isEnterprise")}>
                <Switch value={!!formData?.isEnterprise} />
              </SwitchButton>
            </SwitchContent>
            <SwitchContent className={clsx({ disabled: !formData.isEnterprise })}>
              <BodyText size="medium" weight="semi-bold">
                Deseja pagar por invoice?
              </BodyText>
              <SwitchButton name="shouldPayByInvoice" type="button" onClick={() => handleToggle("shouldPayByInvoice")}>
                <Switch value={!!formData?.shouldPayByInvoice} disabled={!formData.isEnterprise} />
              </SwitchButton>
            </SwitchContent>
          </SwitchContainer>
        )}

        {type === "delete" && <p>Deseja realmente deletar o plano "{initialData?.name}"?</p>}

        <ModalFooterContainer>
          <ButtonContainer>
            <Button
              type="button"
              onClick={handleCloseModal}
              label="Cancelar"
              className={clsx("secondary fill-width")}
            />
          </ButtonContainer>

          <ButtonContainer>
            <Button
              loading={isLoading}
              type="submit"
              onClick={handleSubmit}
              label="Confirmar"
              className={clsx("background-gradient fill-width")}
              disabled={
                (formData.isEnterprise === initialData.isEnterprise &&
                  formData.shouldPayByInvoice === initialData.shouldPayByInvoice) ||
                isLoading
              }
            />
          </ButtonContainer>
        </ModalFooterContainer>
      </ModalContent>
    </Modal>
  )
}

export default AccountsModal
